import { type ReactElement } from 'react'
import { getName, getInitials, isDefined, isNotDefined, isNotEmpty, type NotificationItem, type NotificationTopic } from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { Avatar, Box, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import {
  type MaterialSymbolType,
  getUserColor,
  useMarkNotificationRead,
  useNotificationItemUpdating,
  useNotificationDestination,
} from '@front/model'
import { MaterialSymbolIcon } from '../components/terros/icon/MaterialSymbolIcon'
import { NotificationActions } from './NotificationActions'
import { NotificationActionBadge } from './NotificationActionBadge'
import { notificationRelativeTime } from './helpers'
import { notificationDestinationExplanation, resolveNotificationDestination } from './destination'

const featureIcons: Record<NotificationTopic, MaterialSymbolType> = {
  areas: 'map',
  events: 'event',
  meetings: 'groups',
  recordings: 'mic',
}

type Props = { item: NotificationItem; onOpen?: VoidFunction }

export function NotificationRow({ item, onOpen }: Props): ReactElement {
  const mutation = useMarkNotificationRead()
  const resolver = useNotificationDestination()
  const itemUpdating = useNotificationItemUpdating(item.id)
  const navigate = useNavigate()
  const unread = isNotDefined(item.state.readAt)
  const opacity = unread ? 1 : 0.8
  const actor = item.display.actor
  const account = item.display.account
  const featureIcon = featureIcons[item.topic]
  async function open(): Promise<void> {
    let destination = item.destination
    try {
      destination = await resolver.resolve(destination)
    } catch {
      showNotification({ color: 'red', message: 'Could not open notification. Please try again.' })
      return
    }
    const conversationUnavailable = item.destination?.kind === 'conversation' && destination?.kind === 'notifications'
    try {
      const result = await mutation.update({ ids: [item.id], read: true })
      const updated = result.items.find((row) => row.id === item.id)
      if (!conversationUnavailable) destination = updated?.destination ?? null
    } catch {
      showNotification({ color: 'red', message: 'Could not mark notification read. Please try again.' })
    }
    const explanation = notificationDestinationExplanation(destination)
    if (isDefined(explanation)) showNotification({ message: explanation })
    onOpen?.()
    await navigate({ href: resolveNotificationDestination(destination) })
  }
  return (
    <Box className='border-b border-[var(--mantine-color-default-border)]'>
      <Group gap={8} wrap='nowrap' p={12} align='center'>
        <UnstyledButton
          className='min-w-0 flex-1 rounded focus-visible:outline focus-visible:outline-2'
          onClick={open}
          disabled={mutation.isUpdating || resolver.isResolving || itemUpdating}
          style={{ opacity: item.expired ? 0.6 : opacity }}
        >
          <Group gap={12} wrap='nowrap' align='flex-start'>
            <Box className='relative shrink-0'>
              <Avatar
                src={actor?.avatarUrl}
                alt={getName(actor)}
                size={40}
                variant='filled'
                radius={isDefined(actor) ? '50%' : 'xl'}
                color={isDefined(actor) ? getUserColor(actor) : undefined}
                styles={
                  isDefined(actor) ? undefined : { placeholder: { backgroundColor: 'light-dark(#172f44, #24445f)' } }
                }
              >
                {isDefined(actor) ? getInitials(actor) : <MaterialSymbolIcon icon={featureIcon} size={22} />}
              </Avatar>
              <NotificationActionBadge type={item.type} />
            </Box>
            <Stack gap={2} className='min-w-0 flex-1'>
              <span className='sr-only'>
                {unread ? 'Unread' : 'Read'}
                {item.expired ? ', expired' : ''}
              </span>
              <Group gap={8} wrap='nowrap'>
                {unread && !item.expired && (
                  <Box
                    w={8}
                    h={8}
                    className='shrink-0 rounded-full bg-[var(--mantine-primary-color-filled)]'
                    aria-hidden
                  />
                )}
                <Text size='sm' lh='18px' fw={unread ? 600 : 400} truncate className='min-w-0 flex-1'>
                  {item.display.title}
                </Text>
                <Text size='11px' lh='18px' c='dimmed' className='shrink-0 whitespace-nowrap'>
                  {notificationRelativeTime(item.createdAt)}
                </Text>
              </Group>
              <Text size='sm' lh='18px' lineClamp={isDefined(account) ? 1 : 2}>
                {item.display.body}
              </Text>
              {isDefined(account) && (
                <Group gap={4} wrap='nowrap' c='dimmed'>
                  <MaterialSymbolIcon icon='location_on' size={14} />
                  <Text size='12px' lh='16px' truncate>
                    {isNotEmpty(account.address) ? account.address : 'No address'}
                  </Text>
                </Group>
              )}
            </Stack>
          </Group>
        </UnstyledButton>
        <NotificationActions item={item} />
      </Group>
      {isDefined(mutation.error) && (
        <Text role='alert' size='sm' c='red' px='sm'>
          {mutation.error}
        </Text>
      )}
    </Box>
  )
}
